import { useRouterState } from "@tanstack/react-router";
import { FeatureFeedback } from "@/components/FeatureFeedback";

// Routes where the feedback widget should NOT appear (auth flows, admin,
// legal/system pages, home landing, error/utility pages).
const EXCLUDED_EXACT = new Set<string>([
  "/",
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/reset",
  "/admin-login",
  "/coming-soon",
  "/unsubscribe",
  "/privacy",
  "/terms",
  "/disclaimer",
  "/ai-disclaimer",
  "/cookies",
  "/security",
  "/community-guidelines",
  "/us-privacy-rights",
  "/therapist-terms",
  "/therapist-application",
  "/brand-colors",
]);

const EXCLUDED_PREFIXES = [
  "/admin",
  "/therapist-availability",
  "/sitemap",
  "/api",
];

// Friendly labels for known sections; fallback to titleized slug.
const LABELS: Record<string, string> = {
  "/community": "Community",
  "/journal": "Journal",
  "/dreams": "Dream Journal",
  "/breathing": "Breathing",
  "/grounding": "Grounding",
  "/tapping": "Tapping",
  "/somatic": "Somatic",
  "/yoga": "Yoga",
  "/sounds": "Sounds",
  "/affirmations": "Affirmations",
  "/bilateral": "Bilateral",
  "/shadow-work": "Shadow Work",
  "/sleep": "Sleep",
  "/sleep-dreams": "Sleep & Dreams",
  "/meditations": "Meditations",
  "/check-in": "Daily Check-In",
  "/mood": "Mood Tracker",
  "/guided-journal": "Guided Journal",
  "/": "Toolkit",
  "/favorites": "Saved",
  "/library": "Library",
  "/resources": "Resources",
  "/roadmap": "Explore Topics",
  "/my-healing-journey": "How To Start",
  "/how-to-start": "How To Start",
  "/therapy-approaches": "Therapy Hub",
  "/therapists": "Therapist Directory",
  "/quiz": "Quiz",
  "/events": "Events",
  "/courses": "Courses",
  "/explore": "Explore",
  "/about": "About",
  "/faq": "FAQ",
  "/support": "Support",
  "/help": "Help",
  "/crisis": "Crisis Support",
  "/profile": "Profile",
  "/dashboard": "Dashboard",
  "/membership": "Membership",
  "/enterprise": "Enterprise",
  
  "/guide": "Guide",
  "/flashcards": "Flashcards",
};

function deriveLabel(pathname: string): string | null {
  if (LABELS[pathname]) return LABELS[pathname];
  // Match top-level segment for nested routes (e.g. /healing/anxiety → Healing)
  const top = "/" + pathname.split("/").filter(Boolean)[0];
  if (LABELS[top]) return LABELS[top];
  // No known name for this address (including missing pages): ask nothing
  // rather than inventing a title from the web address.
  return null;
}

export function GlobalFeatureFeedback() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  if (EXCLUDED_EXACT.has(pathname)) return null;
  if (EXCLUDED_PREFIXES.some((p) => pathname.startsWith(p))) return null;

  const label = deriveLabel(pathname);
  if (!label) return null;

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-2">
      <FeatureFeedback key={pathname} feature={label} />
    </div>
  );
}
